import { FC, useCallback, useContext, useMemo, useRef, useState } from 'react';
import './UserOnboardingWizard.scss';
import di from '../../../../../../../di/DependencyInjection';
import { isRight } from 'fp-ts/lib/Either';
import HorizontalSteperComponent from '../../../../../../components/horizontalStepper/HorizontalStepperComponent';
import ProvisionUserUseCase, { ProvisionUserUseCaseName } from '../../../../../../../domain/use_cases/user/ProvisionUserUseCase';
import { ProvisionUserPayload } from '../../../../../../../domain/entities/ProvisionUserEntity';
import Constants, { ConstantsName } from '../../../../../../../constants/Constants';
import { Auth } from 'aws-amplify';
import ModalsContext from '../../../../../../../domain/providers/modal/ModalsContext';
import ModalsContextType from '../../../../../../../domain/providers/modal/ModalsContextType';
import LoadingComponent from '../../../../../../components/LoadingComponent/LoadingComponent';
import ButtonComponent from '../../../../../../components/button/ButtonComponent';
import { ButtonType } from '../../../../../../components/button/ButtonComponentProps';

import { WizardFormData, StepErrors } from './types';
import { WIZARD_STEPS } from './constants';
import {
    validateStepEmail,
    validateStepRole,
    validateStepBUPosition,
    validateStepLocation,
    validateStepTimezone,
} from './validation';
import { computeBucketPaths } from './bucketPath';
import { useWizardDataLoader } from './hooks/useWizardDataLoader';

import Step0_Email from './steps/Step0_Email';
import Step1_Role from './steps/Step1_Role';
import Step2_BUPosition from './steps/Step2_BUPosition';
import Step3_Location from './steps/Step3_Location';
import Step4_Timezone from './steps/Step4_Timezone';
import Step5_Review from './steps/Step5_Review';

// eslint-disable-next-line react/jsx-pascal-case -- File-based naming convention for step components
const StepEmail = Step0_Email;
const StepRole = Step1_Role;
const StepBUPosition = Step2_BUPosition;
const StepLocation = Step3_Location;
const StepTimezone = Step4_Timezone;
const StepReview = Step5_Review;

interface UserOnboardingWizardProps {
    onCsvUploaded: (users: any[]) => void;
}

const INITIAL_FORM_DATA: WizardFormData = {
    email: '',
    firstname: '',
    lastname: '',
    role_id: null,
    roleName: '',
    bu_id: null,
    buName: '',
    position_id: null,
    positionName: '',
    bucketSelectionMode: 'text',
    locations: [],
    clients: [],
    supervisors: [],
    selectedBucketIds: [],
    timezone: '',
};

const SUBMIT_TIMEOUT_MS = 30_000;
const EMAIL_CHECK_TIMEOUT_MS = 10_000;

/**
 * UserOnboardingWizard — Container component that orchestrates the 6-step
 * user provisioning wizard. Owns all form state, handles validation at
 * step boundaries, performs email uniqueness checks, and submits the
 * final payload via ProvisionUserUseCase.
 *
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.6, 1.7, 1.8, 2.3, 2.4, 2.8,
 *              7.3, 7.4, 7.5, 7.6, 7.7, 10.2, 10.3, 10.4
 */
const UserOnboardingWizard: FC<UserOnboardingWizardProps> = ({ onCsvUploaded }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState<WizardFormData>(INITIAL_FORM_DATA);
    const [errors, setErrors] = useState<StepErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCheckingEmail, setIsCheckingEmail] = useState(false);

    const abortControllerRef = useRef<AbortController | null>(null);
    const { addToast } = useContext(ModalsContext) as ModalsContextType;
    const { state: dataState, retry } = useWizardDataLoader();

    // ─── Field Change Handler ─────────────────────────────────────────────
    const handleFieldChange = useCallback((field: keyof WizardFormData, value: any) => {
        setFormData((prev) => {
            const updated = { ...prev, [field]: value };

            // Auto-sync role when position is selected (name-based match)
            // Always updates the role to match the new position
            if (field === 'positionName' && value && dataState.status === 'ready') {
                const posName = (value as string).toLowerCase();
                const matchedRole = dataState.data.roles.find(
                    (r) => r.name.toLowerCase() === posName ||
                           posName.includes(r.name.toLowerCase()) ||
                           r.name.toLowerCase().includes(posName)
                );
                if (matchedRole) {
                    updated.role_id = matchedRole.id;
                    updated.roleName = matchedRole.name;
                } else {
                    // No matching role found — clear the role so user picks manually
                    updated.role_id = null;
                    updated.roleName = '';
                }
            }

            // Auto-preselect client when business unit is selected (BU name = client name)
            if (field === 'buName' && value && dataState.status === 'ready') {
                const buName = (value as string).toLowerCase();
                const matchedClient = dataState.data.clients.find(
                    (c) => c.label.toLowerCase() === buName
                );
                if (matchedClient && updated.clients.length === 0) {
                    updated.clients = [matchedClient.label];
                }
            }

            return updated;
        });
        // Clear error for this field if one exists (Requirement 10.3)
        setErrors((prev) => {
            if (prev[field]) {
                const { [field]: _, ...rest } = prev;
                return rest;
            }
            return prev;
        });
    }, [dataState]);

    // ─── Email Uniqueness Check ───────────────────────────────────────────
    const checkEmailUniqueness = useCallback(async (email: string): Promise<boolean> => {
        // Abort any previous in-flight check
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;
        const timeoutId = setTimeout(() => controller.abort(), EMAIL_CHECK_TIMEOUT_MS);

        try {
            const baseUrl = di.get<Constants>(ConstantsName).HOST_API_USERS_URL;
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();

            const response = await fetch(
                `${baseUrl}/users?email=${encodeURIComponent(email.trim().toLowerCase())}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': token,
                    },
                    signal: controller.signal,
                }
            );

            clearTimeout(timeoutId);

            // 404 = no user found with this email = email is available
            if (response.status === 404) {
                return true;
            }

            if (!response.ok) {
                setErrors((prev) => ({
                    ...prev,
                    email: 'Could not verify email availability. Check your connection.',
                }));
                return false;
            }

            // Read streamed response body fully (matching backend response pattern)
            const reader = response.body?.getReader();
            if (!reader) {
                setErrors((prev) => ({
                    ...prev,
                    email: 'Could not verify email availability. Check your connection.',
                }));
                return false;
            }

            let rawText = '';
            const decoder = new TextDecoder();
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                rawText += decoder.decode(value, { stream: true });
            }
            rawText += decoder.decode();

            const parsed = JSON.parse(rawText);
            // Response shape: { data: [...], total_pages: number }
            const users = parsed?.data ?? [];

            if (users.length > 0) {
                setErrors((prev) => ({
                    ...prev,
                    email: 'This email is already registered',
                }));
                return false;
            }

            return true;
        } catch (err: any) {
            clearTimeout(timeoutId);
            if (err.name === 'AbortError') {
                setErrors((prev) => ({
                    ...prev,
                    email: 'Email check timed out. Please try again.',
                }));
            } else {
                setErrors((prev) => ({
                    ...prev,
                    email: 'Could not verify email availability. Check your connection.',
                }));
            }
            return false;
        }
    }, []);

    // ─── Focus First Invalid Field ────────────────────────────────────────
    const focusFirstInvalidField = useCallback((stepErrors: StepErrors) => {
        const firstErrorField = Object.keys(stepErrors)[0];
        if (firstErrorField) {
            // Try to find and focus the input element by id
            setTimeout(() => {
                const element = document.getElementById(firstErrorField);
                if (element) {
                    element.focus();
                }
            }, 0);
        }
    }, []);

    // ─── Step Validation ──────────────────────────────────────────────────
    const validateCurrentStep = useCallback((): { valid: boolean; errors: StepErrors } => {
        let result;
        switch (currentStep) {
            case 0:
                result = validateStepEmail(formData);
                break;
            case 1:
                result = validateStepBUPosition(formData);
                break;
            case 2:
                result = validateStepRole(formData);
                break;
            case 3:
                result = validateStepLocation(formData, formData.roleName);
                break;
            case 4:
                result = validateStepTimezone(formData);
                break;
            default:
                return { valid: true, errors: {} };
        }

        if (result.valid) {
            return { valid: true, errors: {} };
        }
        return { valid: false, errors: result.errors };
    }, [currentStep, formData]);

    // ─── Handle Next ──────────────────────────────────────────────────────
    const handleNext = useCallback(async () => {
        // Run synchronous step validation (Requirement 10.4)
        const validation = validateCurrentStep();
        if (!validation.valid) {
            setErrors(validation.errors);
            focusFirstInvalidField(validation.errors);
            return;
        }

        // Step 0: additional async email uniqueness check (Requirement 2.3, 2.4, 2.8)
        if (currentStep === 0) {
            setIsCheckingEmail(true);
            const isUnique = await checkEmailUniqueness(formData.email);
            setIsCheckingEmail(false);

            if (!isUnique) {
                // Error already set by checkEmailUniqueness
                focusFirstInvalidField({ email: 'error' });
                return;
            }
        }

        // Clear errors and advance
        setErrors({});
        setCurrentStep((prev) => prev + 1);
    }, [currentStep, formData, validateCurrentStep, checkEmailUniqueness, focusFirstInvalidField]);

    // ─── Handle Back ──────────────────────────────────────────────────────
    const handleBack = useCallback(() => {
        if (currentStep > 0) {
            setErrors({});
            setCurrentStep((prev) => prev - 1);
        }
    }, [currentStep]);

    // ─── Handle Step Click (from stepper header) ──────────────────────────
    const handleChangeStep = useCallback((step: number) => {
        // Only allow backward navigation (Requirement 1.3, 1.8)
        if (step < currentStep) {
            setErrors({});
            setCurrentStep(step);
        }
    }, [currentStep]);

    // ─── Computed Values ──────────────────────────────────────────────────
    const bucketPathPreview = useMemo(() => {
        return computeBucketPaths(
            formData.locations,
            formData.clients,
            formData.supervisors,
            formData.roleName,
            formData.email
        );
    }, [formData.locations, formData.clients, formData.supervisors, formData.roleName, formData.email]);

    const permissionPreview = useMemo(() => {
        if (dataState.status !== 'ready' || !formData.role_id) return [];
        const role = dataState.data.roles.find((r) => r.id === formData.role_id);
        return role?.modules ?? [];
    }, [dataState, formData.role_id]);

    // ─── Handle Submit ────────────────────────────────────────────────────
    const handleSubmit = useCallback(async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);

        // Assemble payload (Requirement 7.3)
        const payload: ProvisionUserPayload = {
            email: formData.email.trim(),
            firstname: formData.firstname.trim(),
            lastname: formData.lastname.trim(),
            role_id: Number(formData.role_id),
            bu_id: Number(formData.bu_id),
            position_id: Number(formData.position_id),
            timezone: formData.timezone,
            location: formData.locations.join(','),
            client: formData.clients.join(','),
            supervisor: formData.supervisors.join(','),
            bucket_ids: formData.selectedBucketIds.length > 0 ? formData.selectedBucketIds : undefined,
        };

        // Timeout wrapper for the submission (Requirement 7.7)
        const timeoutPromise = new Promise<'timeout'>((resolve) => {
            setTimeout(() => resolve('timeout'), SUBMIT_TIMEOUT_MS);
        });

        try {
            const provisionUseCase = di.get<ProvisionUserUseCase>(ProvisionUserUseCaseName);
            const result = await Promise.race([
                provisionUseCase.call(payload),
                timeoutPromise,
            ]);

            if (result === 'timeout') {
                // Requirement 7.7 — timeout
                addToast('Request timed out. Please try again.', 'error', {});
                setIsSubmitting(false);
                return;
            }

            // result is Either<ExceptionEntity, ProvisionUserResponse>
            if (isRight(result)) {
                // Requirement 7.5 — success (HTTP 201)
                const response = result.right;
                const isReactivation = response.message && response.message.toLowerCase().includes('reactivated');

                if (isReactivation) {
                    addToast(
                        `User reactivated: ${formData.email.trim()} was previously deactivated and has been restored with updated configuration (ID: ${response.user_id})`,
                        'default',
                        { duration: 8000 }
                    );
                } else {
                    addToast(
                        `User provisioned successfully: ${response.message || formData.email.trim()} (ID: ${response.user_id})`,
                        'success',
                        { duration: 5000 }
                    );
                }

                // Reset wizard to Step 0, clear all fields
                setFormData(INITIAL_FORM_DATA);
                setErrors({});
                setCurrentStep(0);
                setIsSubmitting(false);
            } else {
                // Requirement 7.6 — error (400/403/409/500)
                const error = result.left;
                const errorMessage = error?.message || 'An error occurred while provisioning the user.';
                addToast(errorMessage, 'error', {});
                setIsSubmitting(false);
            }
        } catch (err: any) {
            // Network or unexpected error
            addToast('An unexpected error occurred. Please try again.', 'error', {});
            setIsSubmitting(false);
        }
    }, [isSubmitting, formData, addToast]);

    // ─── Data Loading States ──────────────────────────────────────────────
    if (dataState.status === 'loading') {
        return (
            <div className="user-onboarding-wizard user-onboarding-wizard--loading">
                <LoadingComponent />
            </div>
        );
    }

    if (dataState.status === 'error') {
        return (
            <div className="user-onboarding-wizard user-onboarding-wizard--error">
                <div className="wizard-error-overlay">
                    <h3>Unable to load wizard data</h3>
                    <p>
                        The following data sources could not be loaded:{' '}
                        <strong>{dataState.failedSources.join(', ')}</strong>
                    </p>
                    <ButtonComponent
                        onClick={retry}
                        text="Retry"
                        type={ButtonType.MAIN}
                    />
                </div>
            </div>
        );
    }

    // dataState.status === 'ready'
    const { data } = dataState;

    // ─── Render Wizard ────────────────────────────────────────────────────
    return (
        <div className="user-onboarding-wizard">
            <HorizontalSteperComponent
                currentStep={currentStep}
                changeStep={handleChangeStep}
                steps={WIZARD_STEPS}
            >
                {[
                    <StepEmail
                        key="step0"
                        formData={formData}
                        errors={errors}
                        onChange={handleFieldChange}
                    />,
                    <StepBUPosition
                        key="step1"
                        formData={formData}
                        errors={errors}
                        businessUnits={data.businessUnits}
                        positions={data.positions}
                        onChange={handleFieldChange}
                    />,
                    <StepRole
                        key="step2"
                        formData={formData}
                        errors={errors}
                        roles={data.roles}
                        onChange={handleFieldChange}
                    />,
                    <StepLocation
                        key="step3"
                        formData={formData}
                        errors={errors}
                        locations={data.locations}
                        clients={data.clients}
                        supervisors={data.supervisors}
                        bucketTree={data.bucketTree}
                        bucketTreeLoading={data.bucketTreeLoading}
                        bucketTreeError={data.bucketTreeError}
                        selectedRole={formData.roleName}
                        onChange={handleFieldChange}
                    />,
                    <StepTimezone
                        key="step4"
                        formData={formData}
                        errors={errors}
                        timezones={data.timezones}
                        onChange={handleFieldChange}
                    />,
                    <StepReview
                        key="step5"
                        formData={formData}
                        roleName={formData.roleName}
                        buName={formData.buName}
                        positionName={formData.positionName}
                        bucketPathPreview={bucketPathPreview}
                        permissionPreview={permissionPreview}
                        isSubmitting={isSubmitting}
                        onSubmit={handleSubmit}
                    />,
                ]}
            </HorizontalSteperComponent>

            {/* Navigation buttons (Requirement 1.6, 1.7) */}
            {currentStep < 5 && (
                <div className="wizard-navigation">
                    {currentStep > 0 && (
                        <ButtonComponent
                            onClick={handleBack}
                            text="Back"
                            type={ButtonType.SECONDARY_OUTLINE}
                        />
                    )}
                    <ButtonComponent
                        onClick={handleNext}
                        text="Next"
                        type={ButtonType.MAIN}
                        disabled={isCheckingEmail}
                        isLoading={isCheckingEmail}
                    />
                </div>
            )}

            {currentStep === 5 && currentStep > 0 && (
                <div className="wizard-navigation">
                    <ButtonComponent
                        onClick={handleBack}
                        text="Back"
                        type={ButtonType.SECONDARY_OUTLINE}
                        disabled={isSubmitting}
                    />
                </div>
            )}
        </div>
    );
};

export default UserOnboardingWizard;
